import { ProcessLogEntry, ProcessLogKind, CollabStatusEntry } from '@/components/agent/types';
import type { AgentProfile } from '@/types/agent';
import { useAgentStore } from '@/stores/agentStore';

export const createLocalId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export const createProcessLog = (
  kind: ProcessLogKind,
  title: string,
  detail?: string,
  status: ProcessLogEntry['status'] = 'completed'
): ProcessLogEntry => ({
  id: createLocalId(),
  kind,
  status,
  title,
  detail,
});

export const finalizeProcessLogs = (logs: ProcessLogEntry[]) =>
  logs.map((log) => {
    // 🔴 v5-#14: 清除 _dedupKey 内部字段，避免持久化到后端 metadata
    const { _dedupKey, ...cleanLog } = log as any;
    if (log.status !== 'active') return cleanLog as ProcessLogEntry;
    return {
      ...cleanLog,
      status: 'completed' as const,
      title: log.title.startsWith('正在') ? log.title.replace(/^正在/, '已完成') : log.title,
    } as ProcessLogEntry;
  });

const THINK_TAG_RE = /\[THINK\]([\s\S]*?)\[\/THINK\]|<think[^>]*>([\s\S]*?)<\/think>/g;
const TOOL_CALL_TAG_RE = /\[TOOL_CALL\]([\s\S]*?)\[\/TOOL_CALL\]|<tool_call[^>]*>([\s\S]*?)<\/tool_call>/g;
const CALL_AGENT_TAG_RE = /\[CALL_AGENT:([^\]]+)\]([\s\S]*?)\[\/CALL_AGENT(?::[^\]]*)?\]/g;
const CALL_AGENT_ASYNC_TAG_RE = /\[CALL_AGENT_ASYNC:([^\]]+)\]([\s\S]*?)\[\/CALL_AGENT_ASYNC(?::[^\]]*)?\]/g;
const COLLABORATION_RESULT_TAG_RE = /\[COLLABORATION_RESULT(?::([^\]]*))?\]([\s\S]*?)\[\/COLLABORATION_RESULT(?::[^\]]*)?\]/g;
const COLLAB_STATUS_TAG_RE = /\[COLLAB_STATUS:(\w+)\]([\s\S]*?)\[\/COLLAB_STATUS\]/g;

export const STATUS_ICONS: Record<string, string> = {
  calling: '📡',
  accepted: '🤝',
  executing: '⚡',
  completed: '✅',
  handoff: '🔄',
  returning: '🔄',
  error: '❌',
  searching: '🔍',
  running: '🟡',
  failed: '❌',
  rejected: '❌',
  timeout: '❌',
  pending: '⏱️',
  cancelled: '⏱️',
  clarifying: '💬',
};

const PHASE_ICONS = STATUS_ICONS;

export const parseCollabStatusTag = (tagContent: string) => {
  const phaseMatch = tagContent.match(/\[COLLAB_STATUS:(\w+)\]([\s\S]*?)\[\/COLLAB_STATUS\]/);
  if (!phaseMatch) return null;

  const phase = phaseMatch[1];
  const inner = phaseMatch[2].trim();
  const parts = inner.split('|').map(s => s.trim()).filter(Boolean);

  return {
    phase,
    icon: parts[0] || PHASE_ICONS[phase] || '📋',
    label: parts[1] || phase,
    detail: parts.slice(2).join(' | '),
  };
};

export const addCollabStatusToLogs = (
  parsed: { phase: string; icon: string; label: string; detail?: string },
  currentLogs: ProcessLogEntry[],
  overrideStatus?: ProcessLogEntry['status'],
): { logs: ProcessLogEntry[]; wasNew: boolean } => {
  const { phase, icon, label, detail } = parsed;
  const dedupKey = `collab_${phase}_${icon}_${label}`;
  if (currentLogs.some(l => (l as any)._dedupKey === dedupKey)) {
    return { logs: currentLogs, wasNew: false };
  }
  const log = createProcessLog('action', `${icon} ${label}`, detail, overrideStatus || (phase === 'executing' ? 'active' : 'completed'));
  (log as any)._dedupKey = dedupKey;
  return { logs: [...currentLogs, log], wasNew: true };
};

const DEFAULT_AGENT_COLOR = '#6366f1';
const DEFAULT_AGENT_AVATAR = '🤖';

const _getAgentProfile = (agentId: string): AgentProfile | undefined => {
  return useAgentStore.getState().agents.find(a => a.id === agentId);
};

export const hasAgentProfile = (agentId: string): boolean => {
  return _getAgentProfile(agentId) !== undefined;
};

export const getAgentName = (agentId: string): string => {
  const profile = _getAgentProfile(agentId);
  if (profile) return profile.name;
  return agentId.replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

export const getAgentAvatar = (agentId: string): string => {
  const profile = _getAgentProfile(agentId);
  return profile?.avatar || DEFAULT_AGENT_AVATAR;
};

export const getAgentColor = (agentId: string): string => {
  const profile = _getAgentProfile(agentId);
  return profile?.color || DEFAULT_AGENT_COLOR;
};

export interface AgentAvatarInfo {
  color: string;
  emoji: string;
}

export const getAgentAvatarInfo = (agentId: string): AgentAvatarInfo => ({
  color: getAgentColor(agentId),
  emoji: getAgentAvatar(agentId),
});

export const DEFAULT_AVATAR_INFO: AgentAvatarInfo = { color: DEFAULT_AGENT_COLOR, emoji: DEFAULT_AGENT_AVATAR };

export const formatAgentIdToName = getAgentName;

export interface AgentCallInfo {
  agentId: string;
  task: string;
  context?: string;
}

export interface CollaborationResultItem {
  callId: string;
  content: string;
  resultType?: string;
  taskId?: string;
}

export interface ExtractedContent {
  processLogs: ProcessLogEntry[];
  thinking: string;
  collabStatuses: CollabStatusEntry[];
  agentCalls: AgentCallInfo[];
  collaborationResults: CollaborationResultItem[];
  cleanText: string;
}

export const extractProcessLogsFromText = (text: string): ProcessLogEntry[] => {
  const extracted = extractAllContentFromText(text);
  return extracted.processLogs;
};

export const extractAllContentFromText = (text: string): ExtractedContent => {
  const processLogs: ProcessLogEntry[] = [];
  let thinking = '';
  const collabStatuses: CollabStatusEntry[] = [];
  const agentCalls: AgentCallInfo[] = [];
  const collaborationResults: CollaborationResultItem[] = [];
  let cleanText = text;

  let match: RegExpExecArray | null;

  THINK_TAG_RE.lastIndex = 0;
  while ((match = THINK_TAG_RE.exec(text)) !== null) {
    const content = (match[1] || match[2] || '').trim();
    if (content) {
      thinking = content;
      processLogs.push(createProcessLog('thinking', '🧠 思考完成', content, 'completed'));
    }
  }
  cleanText = cleanText.replace(THINK_TAG_RE, '');

  TOOL_CALL_TAG_RE.lastIndex = 0;
  while ((match = TOOL_CALL_TAG_RE.exec(text)) !== null) {
    const content = (match[1] || match[2] || '').trim();
    if (content) {
      const firstLine = content.split('\n')[0].slice(0, 80);
      processLogs.push(createProcessLog('action', `⚙️ ${firstLine}`, content, 'completed'));
    }
  }
  cleanText = cleanText.replace(TOOL_CALL_TAG_RE, '');

  CALL_AGENT_TAG_RE.lastIndex = 0;
  while ((match = CALL_AGENT_TAG_RE.exec(text)) !== null) {
    const agentId = match[1];
    const content = match[2].trim();
    const lines = content.split('\n').filter(l => l.trim());
    let task = '';
    let context = '';
    for (const line of lines) {
      const taskMatch = line.match(/^任务[：:]\s*(.*)/);
      const ctxMatch = line.match(/^上下文[：:]\s*(.*)/);
      if (taskMatch) task = taskMatch[1].trim();
      else if (ctxMatch) context = ctxMatch[1].trim();
      else if (!task) task = line.trim();
      else context += (context ? '\n' : '') + line.trim();
    }
    agentCalls.push({ agentId, task, context: context || undefined });
    const agentLabel = formatAgentIdToName(agentId);
    processLogs.push(createProcessLog('action', `📡 调用${agentLabel}`, content, 'completed'));
  }
  cleanText = cleanText.replace(CALL_AGENT_TAG_RE, (fullMatch, p1, p2) => p2.trim());

  CALL_AGENT_ASYNC_TAG_RE.lastIndex = 0;
  while ((match = CALL_AGENT_ASYNC_TAG_RE.exec(text)) !== null) {
    const agentId = match[1];
    const content = match[2].trim();
    const lines = content.split('\n').filter(l => l.trim());
    let task = '';
    let context = '';
    for (const line of lines) {
      const taskMatch = line.match(/^任务[：:]\s*(.*)/);
      const ctxMatch = line.match(/^上下文[：:]\s*(.*)/);
      if (taskMatch) task = taskMatch[1].trim();
      else if (ctxMatch) context = ctxMatch[1].trim();
      else if (!task) task = line.trim();
      else context += (context ? '\n' : '') + line.trim();
    }
    const existingCall = agentCalls.find(ac => ac.agentId === agentId && ac.task === task);
    if (!existingCall) {
      agentCalls.push({ agentId, task, context: context || undefined });
    }
    const agentLabel = formatAgentIdToName(agentId);
    processLogs.push(createProcessLog('action', `📡 异步调用${agentLabel}`, content, 'completed'));
  }
  cleanText = cleanText.replace(CALL_AGENT_ASYNC_TAG_RE, (fullMatch, p1, p2) => p2.trim());

  COLLABORATION_RESULT_TAG_RE.lastIndex = 0;
  while ((match = COLLABORATION_RESULT_TAG_RE.exec(text)) !== null) {
    const callId = match[1] || '';
    let content = match[2].trim();
    let resultType: string | undefined;
    let taskId: string | undefined;

    const resultTypeMatch = content.match(/\[RESULT_TYPE:([^\]]+)\]\n?([\s\S]*?)\n?\[RESULT_TYPE:\1\]/);
    if (resultTypeMatch) {
      resultType = resultTypeMatch[1];
      content = resultTypeMatch[2].trim();
    }

    if (content) {
      try {
        const parsed = JSON.parse(content);
        if (parsed.taskId) taskId = parsed.taskId;
        if (parsed.taskType && !resultType) resultType = parsed.taskType;
      } catch {}

      const firstLine = content.split('\n')[0].slice(0, 80);
      const typeLabel = resultType ? ` (${resultType})` : '';
      processLogs.push(createProcessLog('result', `📋 协作结果${typeLabel}`, content, 'completed'));
      collaborationResults.push({ callId, content, resultType, taskId });
    }
  }
  cleanText = cleanText.replace(COLLABORATION_RESULT_TAG_RE, '');

  COLLAB_STATUS_TAG_RE.lastIndex = 0;
  while ((match = COLLAB_STATUS_TAG_RE.exec(text)) !== null) {
    const phase = match[1];
    const inner = (match[2] || '').trim();
    const parts = inner.split('|').map(s => s.trim()).filter(Boolean);
    collabStatuses.push({
      phase,
      icon: PHASE_ICONS[phase] || '📋',
      label: parts[0] || phase,
      detail: parts.slice(1).join(' | '),
    });
    if (phase === 'calling' && parts.length >= 3) {
      const targetAgentPart = parts[2] || '';
      const agentIdMatch = targetAgentPart.match(/(?:🔍|🎨|🖌️|🤖|🧭|📐|✍️|🎞️|🎬)\s*(.+)/);
      const targetAgentName = agentIdMatch ? agentIdMatch[1].trim() : targetAgentPart;
      const agents = useAgentStore.getState().agents;
      const matched = agents.find(a => a.name === targetAgentName);
      const resolvedAgentId = matched ? matched.id : targetAgentName;
      const taskDesc = parts.slice(3).join(' | ');
      const existingCall = agentCalls.find(ac => ac.agentId === resolvedAgentId);
      if (!existingCall && taskDesc) {
        agentCalls.push({ agentId: resolvedAgentId, task: taskDesc });
      }
    }
  }
  cleanText = cleanText.replace(COLLAB_STATUS_TAG_RE, '');

  cleanText = cleanText.replace(/\[\/?GENERATED_PROMPT\]/g, '');

  return { processLogs, thinking, collabStatuses, agentCalls, collaborationResults, cleanText };
};

export const stripThinkingAndToolCallTags = (text: string, preserveWhitespace = false): string => {
  const result = text
    // 1. 先清除完整的标签对（开闭标签在同一文本中）
    .replace(/<think[^>]*>[\s\S]*?<\/think>/g, '')
    .replace(/\[THINK\][\s\S]*?\[\/THINK\]/g, '')
    .replace(/\[TOOL_CALL\][\s\S]*?\[\/TOOL_CALL\]/g, '')
    .replace(/<tool_call[^>]*>[\s\S]*?<\/tool_call>/g, '')
    .replace(/\[CALL_AGENT:[^\]]*\]([\s\S]*?)\[\/CALL_AGENT(?::[^\]]*)?\]/g, '$1')
    .replace(/\[CALL_AGENT_ASYNC:[^\]]*\]([\s\S]*?)\[\/CALL_AGENT_ASYNC(?::[^\]]*)?\]/g, '$1')
    .replace(/\[ROUTE\][\s\S]*?\[\/ROUTE\]/g, '')
    .replace(/\[REMINDER\][\s\S]*?\[\/REMINDER\]/g, '')
    .replace(/\[COLLAB_CTX:[^\]]*\][\s\S]*?\[\/COLLAB_CTX(?::[^\]]*)?\]/g, '')
    .replace(/\[COLLABORATION_RESULT(?::[^\]]*)?\][\s\S]*?\[\/COLLABORATION_RESULT(?::[^\]]*)?\]/g, '')
    .replace(/\[RESULT_TYPE:[^\]]*\][\s\S]*?\[\/RESULT_TYPE(?::[^\]]*)?\]/g, '')
    .replace(/\[RESULT_TYPE:[^\]]+\]\n?/g, '')
    .replace(/\[COLLAB_STATUS:[^\]]*\][\s\S]*?\[\/COLLAB_STATUS\]/g, '')
    .replace(/\[\/?GENERATED_PROMPT\]/g, '')
    // 2. 清除不完整的 HTML 标签（流式场景中标签跨 chunk，只有开头没有闭合）
    // P0(v10): 只清除开标签本身，不删除后续内容
    // 原正则 /<think[^>]*>[\s\S]*$/g 中 [\s\S]*$ 会删除标签后所有内容，导致摘要截断
    // 完整标签对已在第1步清除，此处只需清除残留的开标签
    .replace(/<think[^>]*>/g, '')
    .replace(/<tool_call[^>]*>/g, '')
    // 孤立的闭合标签（前文已被其他 chunk 处理）
    .replace(/<\/think>/g, '')
    .replace(/<\/tool_call>/g, '')
    // 3. 清除不完整的方括号标签碎片（流式场景中标签可能跨 chunk，只有开头没有闭合）
    .replace(/\[\/?(?:THINK|TOOL_CALL|COLLAB_STATUS(?::[^\]]*)?|ROUTE|REMINDER|COLLAB_CTX(?::[^\]]*)?|COLLABORATION_RESULT(?::[^\]]*)?|RESULT_TYPE(?::[^\]]*)?|CALL_AGENT(?::[^\]]*)?|CALL_AGENT_ASYNC(?::[^\]]*)?|GENERATED_PROMPT)\]/g, '')
    // 4. 清理 LLM 输出的 HTML <br> 标签（表格单元格内常见），转为换行符
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/\n{3,}/g, '\n\n');
  return preserveWhitespace ? result : result.trim();
};

export const TAG_PATTERN = /\[(THINK|TOOL_CALL|CALL_AGENT|CALL_AGENT_ASYNC|COLLABORATION_RESULT|COLLAB_STATUS|ROUTE|REMINDER|COLLAB_CTX|GENERATED_PROMPT)\b/i;
export const ALT_TAG_PATTERN = /<think[^>]*>/;

export const GENERATION_FEATURE_MAP: Record<string, string> = {
  '多机位九宫格': '基于一张参考图生成3×3不同机位/景别的关键帧，画面中不得出现任何文字、序号、编号、水印',
  '剧情推演四宫格': '基于一张参考图生成2×2时间线（生5s/3s）动态演变帧，画面中不得出现任何文字、序号、编号、水印',
  '25宫格连贯分镜': '基于一张图生成连贯的25宫格分镜（5*5），画面中不得出现任何文字、序号、编号、水印',
  '电影级光影校正': '一键仅调整光照/曝光/色温，不改变画面内容',
  '角色三视图生成': '一键生成角色三视图（正面/侧面/背面），画面中不得出现任何文字、序号、编号、水印',
  '画面推演-3秒后': '基于物理逻辑，生成3秒后的动作结果，画面中不得出现任何文字、序号、编号、水印',
  '画面推演-5秒前': '基于物理逻辑，反推5秒前的动作起因，画面中不得出现任何文字、序号、编号、水印',
};

export const calculateNewNodePosition = (
  existingNodes: Array<{ x: number; y: number }>,
  sourceNode?: { x: number; y: number } | null,
): { x: number; y: number } => {
  if (sourceNode) {
    return { x: sourceNode.x + 500, y: sourceNode.y };
  }
  if (existingNodes.length > 0) {
    return {
      x: Math.max(...existingNodes.map((node) => node.x)) + 380,
      y: existingNodes[existingNodes.length - 1]?.y ?? 120,
    };
  }
  return { x: 120, y: 120 };
};
